import { useState } from "react";
import { Flag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const REASONS = [
  "Nudity or sexual content",
  "Hate speech or discrimination",
  "Violence or threats",
  "Harassment or bullying",
  "Spam or misleading content",
  "Impersonation",
  "Copyright infringement",
  "Other",
];

const ReportPostDialog = ({ postId, trigger }: { postId: string; trigger?: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!user) { navigate("/auth"); return; }
    if (!reason) { toast.error("Please select a reason."); return; }
    setLoading(true);

    const { error } = await supabase.from("post_reports" as any).insert({
      post_id: postId,
      reporter_id: user.id,
      reason,
      details,
    } as any);

    setLoading(false);
    if (error?.code === "23505") {
      toast.info("You've already reported this post.");
    } else if (error) {
      toast.error("Something went wrong. Please try again.");
    } else {
      toast.success("Report submitted. Thank you.");
    }
    setOpen(false);
    setReason("");
    setDetails("");
  };

  return (
    <>
      {trigger ? (
        <span onClick={() => setOpen(true)}>{trigger}</span>
      ) : (
        <button onClick={() => setOpen(true)} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors">
          <Flag className="h-3.5 w-3.5" />
          Report
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md z-[200]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Flag className="h-5 w-5 text-destructive" />
            Report Content
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label>Why are you reporting this?</Label>
            <div className="grid grid-cols-2 gap-2">
              {REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`text-left text-xs rounded-lg border px-3 py-2 transition-all ${
                    reason === r
                      ? "border-primary bg-primary/10 text-foreground font-medium"
                      : "border-border bg-secondary/30 text-muted-foreground hover:border-primary/50"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="report-details">Additional details (optional)</Label>
            <Textarea
              id="report-details"
              placeholder="Provide more context about why this content is inappropriate..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={loading || !reason}
            className="w-full"
            variant="destructive"
          >
            {loading ? "Submitting..." : "Submit Report"}
          </Button>
        </div>
      </DialogContent>
      </Dialog>
    </>
  );
};

export default ReportPostDialog;
